import { StudyPlan, Task } from '../types/onboarding';
import { getStudyState } from './studyPlanStore';
import { db } from '../firebase';
import { doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { getPragueDate } from '../utils/pragueDate';

export interface TaskCompletionStatus {
  taskId: string;
  completed: boolean;
  completedAt?: string;
}

export interface MissionTaskProgress {
  dayKey: string;
  tasks: TaskCompletionStatus[];
}

const COMPLETED_TASKS_KEY = 'completedMissionTasks';

/**
 * Get the storage key for today's tasks (Prague timezone)
 */
function getTodayKey(): string {
  return getPragueDate();
}

/**
 * Load completed tasks from localStorage
 */
export function loadCompletedTasks(): MissionTaskProgress {
  const todayKey = getTodayKey();
  const stored = localStorage.getItem(COMPLETED_TASKS_KEY);

  if (stored) {
    try {
      const data = JSON.parse(stored) as MissionTaskProgress;
      // Only return if it's for today
      if (data.dayKey === todayKey) {
        return data;
      }
    } catch (e) {
      console.error('Error loading completed tasks:', e);
    }
  }

  return { dayKey: todayKey, tasks: [] };
}

/**
 * Save completed tasks to localStorage
 */
export function saveCompletedTasks(progress: MissionTaskProgress): void {
  localStorage.setItem(COMPLETED_TASKS_KEY, JSON.stringify(progress));
}

/**
 * Mark a specific task as completed.
 * Writes to localStorage for instant UI feedback.
 * If userId is provided, also syncs to Firestore atomically.
 */
export async function markTaskCompleted(taskId: string, userId?: string): Promise<void> {
  // 1. Write to localStorage (fast, for immediate UI feedback)
  const progress = loadCompletedTasks();
  const existingIndex = progress.tasks.findIndex(t => t.taskId === taskId);

  if (existingIndex >= 0) {
    progress.tasks[existingIndex].completed = true;
    progress.tasks[existingIndex].completedAt = new Date().toISOString();
  } else {
    progress.tasks.push({
      taskId,
      completed: true,
      completedAt: new Date().toISOString()
    });
  }

  saveCompletedTasks(progress);
  console.log(`[MissionTasks] Task ${taskId} marked as completed`);

  // 2. Sync to Firestore atomically if userId is provided
  if (userId) {
    try {
      const studyDocRef = doc(db, 'users', userId, 'study', 'onboarding');
      await updateDoc(studyDocRef, {
        'studyPlan.missionState.completedTaskIds': arrayUnion(taskId),
        'missionState.completedTaskIds': arrayUnion(taskId)
      });
      console.log(`[MissionTasks] Synced task ${taskId} to Firestore`);
    } catch (error) {
      console.error('[MissionTasks] Failed to sync task completion to Firestore:', error);
      // Don't throw - localStorage write already succeeded
    }
  }

  // 3. Trigger celebration event
  console.log('[MissionTasks] Dispatching taskCelebration event');
  const event = new CustomEvent('taskCelebration', { detail: { taskId } });
  window.dispatchEvent(event);
  console.log('[MissionTasks] Event dispatched');
}

/**
 * Check if a specific task is completed
 */
export function isTaskCompleted(taskId: string): boolean {
  const progress = loadCompletedTasks();
  return progress.tasks.some(t => t.taskId === taskId && t.completed);
}

/**
 * Get completion status for all tasks in today's mission
 */
export function getTodayMissionProgress(plan: StudyPlan | null): TaskCompletionStatus[] {
  if (!plan?.todayMission?.tasks) return [];

  const progress = loadCompletedTasks();

  return plan.todayMission.tasks.map(task => {
    const completedTask = progress.tasks.find(t => t.taskId === task.id);
    return {
      taskId: task.id,
      completed: !!completedTask?.completed,
      completedAt: completedTask?.completedAt
    };
  });
}

/**
 * Check if all tasks for today are completed
 */
export function areAllTasksCompleted(plan: StudyPlan | null): boolean {
  if (!plan?.todayMission?.tasks || plan.todayMission.tasks.length === 0) return false;

  const progress = getTodayMissionProgress(plan);
  return progress.every(t => t.completed);
}

/**
 * Get the first uncompleted task
 */
export function getFirstUncompletedTask(plan: StudyPlan | null): Task | null {
  if (!plan?.todayMission?.tasks) return null;

  return plan.todayMission.tasks.find(task => !isTaskCompleted(task.id)) || null;
}

/**
 * Clear all task completions (for testing or new day)
 */
export function clearTaskCompletions(): void {
  localStorage.removeItem(COMPLETED_TASKS_KEY);
}

/**
 * Auto-complete a task based on content completion
 * This connects actual content progress to mission tasks
 */
export async function autoCompleteTaskFromContent(
  userId: string,
  contentType: 'chapter' | 'flashcards' | 'quiz' | 'practice',
  contentId: string
): Promise<void> {
  try {
    const state = await getStudyState(userId);
    if (!state?.studyPlan) return;

    const plan = state.studyPlan;
    const todayTasks = plan.todayMission.tasks;

    console.log(`[MissionTasks] Looking for task: contentType=${contentType}, contentId=${contentId}`);
    console.log(`[MissionTasks] Available tasks:`, todayTasks.map(t => ({
      id: t.id,
      kind: t.kind,
      deepLink: t.deepLink,
      linkHasContentId: t.deepLink.includes(contentId),
      contentIdHasLinkPart: t.deepLink.split('chapter=')[1] ? contentId.includes(t.deepLink.split('chapter=')[1]) : false
    })));

    // Find a matching task based on content type and ID
    // First, try to match by both kind AND deepLink content ID (more specific)
    let matchingTask = todayTasks.find(task => {
      // For quiz tasks, look for recap kind with matching chapter ID
      if (contentType === 'quiz' && task.kind === 'recap' && task.deepLink.includes(contentId)) {
        console.log(`[MissionTasks] Found match by kind (recap) + deepLink: ${task.id}`);
        return true;
      }

      // For chapter tasks, look for chapter kind with matching chapter ID
      if (contentType === 'chapter' && task.kind === 'chapter') {
        // Try precise match first
        if (task.deepLink.includes(contentId)) {
          console.log(`[MissionTasks] Found match by kind (chapter) + deepLink: ${task.id}`);
          return true;
        }

        // Try loose match (if contentId is part of link or vice versa)
        if (contentId.includes(task.deepLink.split('chapter=')[1]) ||
          (task.deepLink.split('chapter=')[1] && task.deepLink.split('chapter=')[1].includes(contentId))) {
          console.log(`[MissionTasks] Found loose match by contentId: ${contentId} vs deepLink: ${task.deepLink}`);
          return true;
        }
      }

      // For flashcard tasks, look for flashcards kind
      if (contentType === 'flashcards' && task.kind === 'flashcards') {
        console.log(`[MissionTasks] Found match by kind (flashcards): ${task.id}`);
        return true;
      }

      return false;
    });

    // If no specific match found, fall back to just deepLink matching
    if (!matchingTask) {
      matchingTask = todayTasks.find(task => {
        if (task.deepLink.includes(contentId)) {
          console.log(`[MissionTasks] Found match by deepLink only: ${task.id}`);
          return true;
        }
        return false;
      });
    }

    if (matchingTask) {
      console.log(`[MissionTasks] Matched task: ${matchingTask.id}, completed: ${isTaskCompleted(matchingTask.id)}`);
      if (!isTaskCompleted(matchingTask.id)) {
        await markTaskCompleted(matchingTask.id, userId);
        console.log(`[MissionTasks] Auto-completed task ${matchingTask.id} from ${contentType} completion`);
      } else {
        console.log(`[MissionTasks] Task ${matchingTask.id} already completed`);
      }
    } else {
      console.log(`[MissionTasks] No matching task found`);
    }
  } catch (error) {
    console.error('[MissionTasks] Error auto-completing task:', error);
  }
}
